import ConfirmModal from "components/shared-components/ConfirmModal";
import RemoteDataTable from "components/shared-components/RemoteTable/data-table";
import {
  CUSTOM_SELECT_FILTER_TABLE_TYPE,
  DELAY_FILTER_TIME,
} from "constants/RemoteDataTableContanst";
import { CheckActionAccessList } from "helpers/utils";
import {
  useDeleteCustomerLocationTransport,
  useListCustomerLocationTransport,
} from "hooks/customer/useCustomer";
import { useListTransport } from "hooks/transport/useTransport";
import React, { useEffect, useMemo, useState } from "react";
import { customFilter, textFilter } from "react-bootstrap-table2-filter";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import {
  Button,
  Card,
  CardBody,
  Col,
  Row,
  Dropdown,
  DropdownMenu,
  DropdownItem,
  DropdownToggle,
} from "reactstrap";
import useCustomHook from "../useCustomHook";
import CreateLocationTransportModal from "./CreateLocationTransportModal";
import UpdateLocationTransportModal from "./UpdateLocationTransportModal";
import { useDistricts, useProvinces, useWards } from "hooks/";
import { mapOptionObjectToArray } from "pages/HubManagement/utils";

const useMergedProvinces = (showAllLevels, is_level_2) => {
  const [provinceParams2Level, setProvinceParams2Level] = useState({
    countryId: 1,
    type: 1,
    parent_code: "",
    record_status: "O",
  });

  const [provinceParams3Level, setProvinceParams3Level] = useState({
    countryId: 1,
    type: 1,
    parent_code: "",
    record_status: "C",
  });

  const { data: provinces2Level, refetch: refetchProvinces2Level } =
    useProvinces(provinceParams2Level);
  const { data: provinces3Level, refetch: refetchProvinces3Level } =
    useProvinces(provinceParams3Level);

  // Merge provinces từ cả 2 cấp
  const mergedProvinces = useMemo(() => {
    const provinces2LevelArray = (
      mapOptionObjectToArray(provinces2Level) ?? []
    ).map(p => ({ ...p, level: 2 }));

    const provinces3LevelArray = (
      mapOptionObjectToArray(provinces3Level) ?? []
    ).map(p => ({ ...p, level: 3 }));

    if (showAllLevels) {
      const merged = [...provinces2LevelArray];
      provinces3LevelArray.forEach(p3 => {
        if (!merged.find(p => p.value === p3.value)) merged.push(p3);
      });

      return merged.sort((a, b) => {
        if (a.level !== b.level) {
          return a.level === 3 ? -1 : 1;
        }
        return a.label.localeCompare(b.label);
      });
    }

    const target = is_level_2 ? provinces2LevelArray : provinces3LevelArray;
    return target.sort((a, b) => a.label.localeCompare(b.label));
  }, [provinces2Level, provinces3Level, showAllLevels, is_level_2]);

  useEffect(() => {
    if (showAllLevels) {
      // Load cả 2 cấp
      refetchProvinces2Level();
      refetchProvinces3Level();
    } else if (is_level_2) {
      // Chỉ load 2 cấp
      refetchProvinces2Level();
    } else {
      // Chỉ load 3 cấp
      refetchProvinces3Level();
    }
  }, [
    showAllLevels,
    is_level_2,
    refetchProvinces2Level,
    refetchProvinces3Level,
  ]);

  return mergedProvinces;
};

const useCustomWardsFor2Level = (province_code, is_level_2, showAllLevels) => {
  const [wardParams2Level, setWardParams2Level] = useState({
    countryId: 1,
    type: 2,
    parent_code: "",
    record_status: "O",
  });

  const { data: wards2Level, refetch: refetchWards2Level } =
    useWards(wardParams2Level);

  useEffect(() => {
    if (province_code && (is_level_2 || showAllLevels)) {
      const selectedProvinceLevel = province_code.level;
      const record_status = selectedProvinceLevel === 2 ? "O" : "C";

      setWardParams2Level({
        countryId: 1,
        type: 2,
        parent_code: province_code.value,
        record_status: record_status,
      });
    } else {
      setWardParams2Level({
        countryId: 1,
        type: 2,
        parent_code: "",
        record_status: "O",
      });
    }
  }, [province_code, is_level_2, showAllLevels]);

  useEffect(() => {
    if (wardParams2Level.parent_code) {
      refetchWards2Level();
    }
  }, [wardParams2Level, refetchWards2Level]);

  const wardOptions2Level = useMemo(() => {
    return mapOptionObjectToArray(wards2Level);
  }, [wards2Level]);

  return wardOptions2Level;
};

const CustomerLocationTransport = ({ customerId }) => {
  const { t } = useTranslation(["customer"]);

  const [addressLevelFilter, setAddressLevelFilter] = useState({
    value: "all",
    label: "Tất cả",
  });
  const [isOpenAddrFilter, setIsOpenAddrFilter] = useState(false);

  const addressLevelFilterOptions = [
    { value: "all", label: "Tất cả" },
    { value: "2", label: "2 cấp" },
    { value: "3", label: "3 cấp" },
  ];

  const is_level_2 = addressLevelFilter.value === "2";
  const showAllLevels = addressLevelFilter.value === "all";

  const [isOpenUpdateModal, setIsOpenUpdateModal] = useState(false);
  const [isOpenCreateModal, setIsOpenCreateModal] = useState(false);
  const [isOpenConfirmDeleteModal, setIsOpenConfirmDeleteModal] =
    useState(false);
  const [choosenData, setChoosenData] = useState(null);
  const [choosenDeleteId, setChoosenDeleteId] = useState(null);

  const [params, setParams] = useState({
    page: 1,
    sizePerPage: 10,
    columnFilter: {
      "filter[record_status]": "O",
      "filter[customer_id]": customerId,
      ...(showAllLevels
        ? {}
        : {
            "filter[is_new_address]": is_level_2 ? "N" : "O",
          }),
    },
    fields:
      "id, province_code, province, district_code, district, ward, ward_code, record_status, transport, customer_id, hub_id",
    expand: "hub",
  });

  const filterForm = useForm({
    mode: "all",
    reValidateMode: "onChange",
  });

  const { control, watch } = filterForm;
  const province_code = watch("province_code");
  const district_code = watch("district_code");
  const ward_code = watch("ward_code");
  const transport_id = watch("transport_id");

  const provinceOptions = useMergedProvinces(showAllLevels, is_level_2);

  const wardOptions2Level = useCustomWardsFor2Level(
    province_code,
    is_level_2,
    showAllLevels
  );

  const {
    districtOptions,
    wardOptions: wardOptions3Level,
    isDisableDistrictSelect,
    isDisableWardSelect,
  } = useCustomHook({
    mainForm: filterForm,
    isStepable: true,
    is_level_2: showAllLevels ? null : is_level_2,
  });

  const selectedProvinceLevel = province_code?.level;

  const isDistrictDisabled = useMemo(() => {
    if (!province_code) return true;

    if (showAllLevels) {
      return selectedProvinceLevel === 2;
    }

    return is_level_2;
  }, [province_code, showAllLevels, selectedProvinceLevel, is_level_2]);

  const isWardDisabled = useMemo(() => {
    if (!province_code) return true;

    if (showAllLevels) {
      return selectedProvinceLevel === 3 && !district_code;
    }

    return is_level_2 ? false : !district_code;
  }, [
    province_code,
    showAllLevels,
    selectedProvinceLevel,
    is_level_2,
    district_code,
  ]);

  const districtOptionsFinal = useMemo(() => {
    if (showAllLevels) {
      return selectedProvinceLevel === 3 ? districtOptions : [];
    }
    return is_level_2 ? [] : districtOptions;
  }, [showAllLevels, selectedProvinceLevel, is_level_2, districtOptions]);

  const wardOptions = useMemo(() => {
    if (showAllLevels) {
      return selectedProvinceLevel === 2
        ? wardOptions2Level
        : wardOptions3Level;
    }
    return is_level_2 ? wardOptions2Level : wardOptions3Level;
  }, [
    showAllLevels,
    selectedProvinceLevel,
    is_level_2,
    wardOptions2Level,
    wardOptions3Level,
  ]);

  useEffect(() => {
    setParams(prev => ({
      ...prev,
      page: 1,
      columnFilter: {
        ...prev.columnFilter,
        "filter[is_new_address]": showAllLevels
          ? undefined
          : is_level_2
          ? "N"
          : "O",
      },
    }));
  }, [addressLevelFilter, showAllLevels, is_level_2]);

  // Handle dropdown change
  const handleAddressLevelFilterChange = selectedOption => {
    setAddressLevelFilter(selectedOption);

    // Clear all form values when switching address level
    filterForm.setValue("province_code", null);
    filterForm.setValue("district_code", null);
    filterForm.setValue("ward_code", null);
    filterForm.setValue("transport_id", null);

    // Update params based on selected filter
    const newIsLevel2 = selectedOption.value === "2";
    const newShowAllLevels = selectedOption.value === "all";

    setParams(prev => ({
      ...prev,
      page: 1,
      columnFilter: {
        ...prev.columnFilter,
        "filter[is_new_address]": newShowAllLevels
          ? undefined
          : newIsLevel2
          ? "N"
          : "O",
        "filter[province_code]": undefined,
        "filter[district_code]": undefined,
        "filter[ward_code]": undefined,
        "filter[transport_id]": undefined,
      },
    }));
  };

  // Remove lower-level filters when removing a higher-level filter
  useEffect(() => {
    if (!province_code) {
      filterForm.setValue("district_code", null);
      filterForm.setValue("ward_code", null);
    }
  }, [province_code, filterForm]);

  useEffect(() => {
    if (showAllLevels) {
      if (selectedProvinceLevel === 3 && !district_code) {
        filterForm.setValue("ward_code", null);
      }
    } else if (!is_level_2 && !district_code) {
      filterForm.setValue("ward_code", null);
    }
  }, [
    district_code,
    is_level_2,
    showAllLevels,
    selectedProvinceLevel,
    filterForm,
  ]);

  const {
    data: customerLocationTransports,
    refetch: fetchCustomerLocationTransports,
    isLoading,
  } = useListCustomerLocationTransport({ params });

  const transportParams = useMemo(
    () => ({
      sizePerPage: 100,
      columnFilter: {
        "filter[record_status]": "O",
      },
      fields: "id,name",
    }),
    [is_level_2, showAllLevels]
  );

  const { data: transportList, refetch: fetchTransportList } = useListTransport(
    { params: transportParams }
  );

  const filteredData = useMemo(() => {
    if (!customerLocationTransports?.items) {
      return [];
    }

    return customerLocationTransports.items;
  }, [customerLocationTransports?.items]);

  const getAddressLevel = location => {
    const hasWardCode = location.ward_code && location.ward_code.trim() !== "";

    if (location.district_code && !hasWardCode) {
      return "2 cấp";
    }

    if (location.district_code && hasWardCode) {
      return "3 cấp";
    }

    const isLevel2 =
      location.is_new_address === "N" ||
      location.from_hub_is_new_address === "N" ||
      location.to_hub_is_new_address === "N" ||
      location.is_level_2 === true;

    return isLevel2 ? "2 cấp" : "3 cấp";
  };

  const columnTable = [
    {
      dataField: "id",
      text: t("customer_location_transport_table.no"),
      formatter: (cell, row, enumObject) => {
        return `${enumObject + 1}`;
      },
    },
    {
      dataField: "address_level",
      text: "Loại địa chỉ",
      hidden: !showAllLevels,
      formatter: (cell, row) => {
        return (
          <div style={{ textAlign: "center" }}>
            <span
              className={`badge ${
                getAddressLevel(row) === "2 cấp" ? "bg-info" : "bg-secondary"
              }`}
              style={{
                fontSize: "11px",
                padding: "4px 8px",
              }}
            >
              {getAddressLevel(row)}
            </span>
          </div>
        );
      },
      headerStyle: {
        textAlign: "center",
        minWidth: "100px",
      },
    },
    {
      dataField: "province_code",
      text: t("customer_location_transport_table.province"),
      formatter: (_cell, row) => {
        return row?.province?.name;
      },
      filter: customFilter({
        type: CUSTOM_SELECT_FILTER_TABLE_TYPE,
      }),
      filterRenderer: (onFilter, column) => {
        return (
          <div style={{ minWidth: "200px", fontWeight: "normal" }}>
            <Controller
              control={control}
              name="province_code"
              render={({ field }) => (
                <Select
                  {...field}
                  placeholder={t("placeholder.select", {
                    title: t("customer_location_transport_table.province"),
                  })}
                  options={provinceOptions}
                  isSearchable
                  className="basic-single w-100"
                  classNamePrefix="select"
                  isClearable
                />
              )}
            />
          </div>
        );
      },
    },
    {
      dataField: "district_code",
      text: t("customer_location_transport_table.district"),
      hidden: is_level_2 && !showAllLevels,
      formatter: (_cell, row) => {
        const isRowLevel2 = getAddressLevel(row) === "2 cấp";
        return isRowLevel2 ? "" : row?.district?.name;
      },
      filter: customFilter({
        type: CUSTOM_SELECT_FILTER_TABLE_TYPE,
      }),
      filterRenderer: (onFilter, column) => {
        return (
          <div style={{ minWidth: "200px", fontWeight: "normal" }}>
            <Controller
              control={control}
              name="district_code"
              render={({ field }) => (
                <Select
                  {...field}
                  placeholder={t("placeholder.select", {
                    title: t("customer_location_transport_table.district"),
                  })}
                  options={districtOptionsFinal}
                  isDisabled={isDistrictDisabled}
                  isSearchable
                  className="basic-single w-100"
                  classNamePrefix="select"
                  isClearable
                />
              )}
            />
          </div>
        );
      },
    },
    {
      dataField: "ward_code",
      text: "Phường xã",
      formatter: (_cell, row) => {
        const isRowLevel2 = getAddressLevel(row) === "2 cấp";

        if (isRowLevel2) {
          return row?.district?.name;
        } else {
          return row?.ward?.name;
        }
      },
      filter: customFilter({
        type: CUSTOM_SELECT_FILTER_TABLE_TYPE,
      }),
      filterRenderer: (onFilter, column) => {
        return (
          <div style={{ minWidth: "200px", fontWeight: "normal" }}>
            <Controller
              control={control}
              name="ward_code"
              render={({ field }) => (
                <Select
                  {...field}
                  placeholder={t("placeholder.select", {
                    title: "Phường xã",
                  })}
                  options={wardOptions}
                  isDisabled={isWardDisabled}
                  isSearchable
                  className="basic-single w-100"
                  classNamePrefix="select"
                  isClearable
                />
              )}
            />
          </div>
        );
      },
    },
    {
      dataField: "transport_id",
      text: t("customer_location_transport_table.transport"),
      formatter: (_cell, row) => {
        return row?.transport?.name;
      },
      filter: customFilter({
        type: CUSTOM_SELECT_FILTER_TABLE_TYPE,
      }),
      filterRenderer: (onFilter, column) => {
        return (
          <div style={{ minWidth: "200px", fontWeight: "normal" }}>
            <Controller
              control={control}
              name="transport_id"
              render={({ field }) => (
                <Select
                  {...field}
                  placeholder={t("placeholder.select", {
                    title: t("customer_location_transport_table.transport"),
                  })}
                  options={optionsTransportSelect || []}
                  isSearchable
                  className="basic-single w-100"
                  classNamePrefix="select"
                  isClearable
                />
              )}
            />
          </div>
        );
      },
    },
    {
      dataField: "hub_name",
      text: t("customer_location_transport_table.hub"),
      sort: true,
      formatter: (_cell, row) => {
        return row?.hub?.name;
      },
      filter: textFilter({
        delay: DELAY_FILTER_TIME,
        placeholder: t("placeholder.enter", {
          title: t("customer_location_transport_table.hub"),
        }),
      }),
    },
  ];
  const listRowAction = [
    {
      actionAccess: "",
      icon: <i className="fas fa-pen fa-lg text-primary ms-2" />,
      text: t("customer_location_transport_table.actions.update"),
      onClick: row => {
        setChoosenData(row);
        toggleUpdateModal();
      },
    },
    {
      actionAccess: "",
      icon: <i className="fas fa-trash fa-lg ms-2 text-danger" />,
      text: t("customer_location_transport_table.actions.delete"),
      conditionVisible: {
        record_status: ["O"],
      },
      onClick: row => {
        setChoosenDeleteId(row.id);
        setIsOpenConfirmDeleteModal(true);
      },
    },
  ];
  const rowActionList = CheckActionAccessList(listRowAction, "actionAccess");

  const onTableChange = (_type, value) => {
    setParams(prev => ({
      ...prev,
      page: value.page,
      sizePerPage: value.sizePerPage,
      sort: `${value.sortOrder}${value.sortField}`,
      columnFilter: {
        ...prev.columnFilter,
        hub_name: value.columnFilter["filter[hub_name][like]"],
      },
    }));
  };

  // Update params when filter values change
  useEffect(() => {
    const isProvinceLevel2 = showAllLevels
      ? selectedProvinceLevel === 2
      : is_level_2;

    const wardFilterValue = ward_code ? ward_code.value : undefined;

    setParams(prev => ({
      ...prev,
      columnFilter: {
        ...prev.columnFilter,
        "filter[province_code]": province_code
          ? province_code.value
          : undefined,
        "filter[district_code]": isProvinceLevel2
          ? wardFilterValue
          : district_code
          ? district_code.value
          : undefined,
        "filter[ward_code]": isProvinceLevel2 ? undefined : wardFilterValue,
        "filter[transport_id]": transport_id ? transport_id.value : undefined,
      },
    }));
  }, [
    province_code,
    district_code,
    ward_code,
    transport_id,
    is_level_2,
    showAllLevels,
    selectedProvinceLevel,
  ]);

  // Refetch transport list when address level changes
  useEffect(() => {
    fetchTransportList();
  }, [is_level_2, showAllLevels, fetchTransportList]);

  const onSuccessDelele = () => {
    toggleDeleteModal("close");
    fetchCustomerLocationTransports();
  };

  const { mutate: deleteCustomerLocationTransport } =
    useDeleteCustomerLocationTransport(onSuccessDelele);

  useEffect(() => {
    fetchCustomerLocationTransports();
  }, [params]);

  const toggleUpdateModal = type => {
    if (type && type === "close") {
      setChoosenData(null);
    }
    setIsOpenUpdateModal(prev => !prev);
  };

  const toggleCreateModal = type => {
    setIsOpenCreateModal(prev => !prev);
  };

  const toggleDeleteModal = type => {
    if (type && type === "close") {
      setChoosenDeleteId(null);
    }
    setIsOpenConfirmDeleteModal(prev => !prev);
  };

  const optionsTransportSelect = useMemo(() => {
    return transportList?.items?.map(item => ({
      label: item.name,
      value: item.id,
    }));
  }, [transportList]);

  const onDeleteTransport = () => {
    deleteCustomerLocationTransport({ id: choosenDeleteId });
  };

  return (
    <>
      <Card>
        <CardBody>
          <Row className="w-100 mb-3">
            <Col className="d-flex align-items-center gap-3">
              <Button
                className="btn"
                color="primary"
                onClick={toggleCreateModal}
              >
                {t("customer_location_transport_table.add_new")}
              </Button>

              <div style={{ minWidth: 100 }}>
                <Dropdown
                  isOpen={isOpenAddrFilter}
                  toggle={() => setIsOpenAddrFilter(!isOpenAddrFilter)}
                >
                  <DropdownToggle
                    className="btn primary-btn w-100 text-overflow"
                    caret
                  >
                    Địa chỉ
                    <span>:&nbsp;{addressLevelFilter.label}</span>
                    <i className="mdi mdi-chevron-down" />
                  </DropdownToggle>
                  <DropdownMenu className="w-100" style={{ minWidth: 100 }}>
                    {addressLevelFilterOptions.map(opt => (
                      <DropdownItem
                        key={opt.value}
                        onClick={() => handleAddressLevelFilterChange(opt)}
                      >
                        {opt.label}
                      </DropdownItem>
                    ))}
                  </DropdownMenu>
                </Dropdown>
              </div>
            </Col>
          </Row>
          <RemoteDataTable
            keyField="id"
            loading={isLoading}
            data={filteredData}
            columns={columnTable}
            listRowAction={rowActionList}
            page={params.page}
            onTableChange={onTableChange}
            isShowRowIndex={false}
            totalSize={customerLocationTransports?.totalCount || 0}
            isShowSearchAll={false}
            isShowPagination={customerLocationTransports?.pageCount > 1}
          ></RemoteDataTable>
        </CardBody>
        <ConfirmModal
          isOpen={isOpenConfirmDeleteModal}
          titleHeader={t(
            "customer_location_transport_table.actions.confirm.titleUnActiveTransport"
          )}
          titleBody={t(
            "customer_location_transport_table.actions.confirm.contentUnActiveTransport"
          )}
          onConfirm={onDeleteTransport}
          textBtnConfirm={t(
            "customer_location_transport_table.actions.confirm.confirmBtn"
          )}
          textBtnCancel={t(
            "customer_location_transport_table.actions.confirm.cancelBtn"
          )}
          toggle={() => toggleDeleteModal("close")}
        />
        <UpdateLocationTransportModal
          customerId={customerId}
          choosenData={choosenData}
          isOpenUpdateModal={isOpenUpdateModal}
          toggleUpdateModal={toggleUpdateModal}
          fetchCustomerLocationTransports={fetchCustomerLocationTransports}
        />
        <CreateLocationTransportModal
          customerId={customerId}
          isOpenCreateModal={isOpenCreateModal}
          toggleCreateModal={toggleCreateModal}
          fetchCustomerLocationTransports={fetchCustomerLocationTransports}
        />
      </Card>
    </>
  );
};

export default CustomerLocationTransport;
